import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraHubGrid } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Crosshair, FileText, Flag, Layers, LayoutGrid, ListChecks, Target } from 'lucide-react';
import { useEffect } from 'react';
import { PageBody, PageHero } from '../../components/layout';
import { useRecordShellEnabled } from '../../components/RecordShell/recordShellFlag';
import { useRecordBreadcrumbs } from '../../components/RecordShell/useRecordBreadcrumbs';
import { useWorkflowAreas } from '../../lib/access/useWorkflowAreas';
import { renderRouterLink } from '../../lib/renderRouterLink';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { growthCrumbs } from './growthCrumbs';
/** Today's three cards: kept unchanged while growth_record_shell_v1 is off. */ const legacySections = [
    {
        href: '/capture',
        testid: Testids.growth.overviewCardPursuits,
        icon: Crosshair,
        title: 'Pursuits',
        description: 'Deal threads for the bids you are chasing, from first signal to submission.',
        area: 'capture'
    },
    {
        href: '/opportunities',
        testid: Testids.growth.overviewCardOpportunities,
        icon: Target,
        title: 'Opportunities',
        description: 'Federal notices and tracked opportunities worth a look.',
        area: 'opportunities'
    },
    {
        href: '/landscapes',
        testid: Testids.growth.overviewCardLandscapes,
        icon: Layers,
        title: 'Landscapes',
        description: 'Market views that group opportunities by agency, vehicle, or theme.',
        area: 'opportunities'
    }
];
/** GRW-03: the six mockup destinations (growth.js:84-89), in mockup order. */ const sections = [
    {
        href: '/growth/home',
        testid: Testids.growth.overviewCardHome,
        icon: LayoutGrid,
        title: 'Growth Home',
        description: "Your pursuits, what's due, and what changed since you last looked.",
        area: 'opportunities'
    },
    {
        href: '/capture',
        testid: Testids.growth.overviewCardPursuits,
        icon: Crosshair,
        title: 'Pursuits',
        description: 'Deal threads for the bids you are chasing, from first signal to submission.',
        area: 'capture'
    },
    {
        href: '/opportunities',
        testid: Testids.growth.overviewCardOpportunities,
        icon: Flag,
        title: 'Opportunities',
        description: 'Federal notices and tracked opportunities worth a look.',
        area: 'opportunities'
    },
    {
        href: '/growth/rfi-responses',
        testid: Testids.growth.overviewCardRfiResponses,
        icon: ListChecks,
        title: 'RFI responses',
        description: 'Workspaces for answering RFIs and sources sought, question by question.',
        area: 'capture'
    },
    {
        href: '/growth/proposals',
        testid: Testids.growth.overviewCardProposals,
        icon: FileText,
        title: 'Proposals',
        description: 'RFP workspaces: compliance, volumes, reviews and pricing.',
        area: 'projects'
    },
    {
        href: '/landscapes',
        testid: Testids.growth.overviewCardLandscapes,
        icon: Layers,
        title: 'Landscapes',
        description: 'Market views that group pursuits and contracts by agency, vehicle or theme, over years.',
        area: 'opportunities'
    }
];
export function GrowthHubPage() {
    const { reachable } = useWorkflowAreas();
    const shell = useRecordShellEnabled();
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    useEffect(()=>{
        setShowHeader(false);
        return ()=>setShowHeader(true);
    }, [
        setShowHeader
    ]);
    useRecordBreadcrumbs(shell ? growthCrumbs() : null);
    const cards = shell ? sections : legacySections;
    const items = cards.filter((section)=>reachable[section.area]).map((section)=>{
        const Icon = section.icon;
        return {
            id: section.href,
            title: section.title,
            description: section.description,
            icon: /*#__PURE__*/ _jsx(Icon, {
                className: "h-5 w-5"
            }),
            href: section.href
        };
    });
    return /*#__PURE__*/ _jsxs(PageBody, {
        children: [
            /*#__PURE__*/ _jsx(PageHero, {
                title: "Growth",
                subtitle: shell ? 'Pursuits, the workspaces you answer the government in, and the markets they sit in' : 'Pursuits, opportunities, and landscapes'
            }),
            /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.growth.overviewPage,
                children: /*#__PURE__*/ _jsx(SpecraHubGrid, {
                    items: items,
                    renderLink: renderRouterLink,
                    columns: 3,
                    getItemTestId: (item)=>cards.find((s)=>s.href === item.id)?.testid
                })
            })
        ]
    });
}
